import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { articles, categories } from '@/mocks/moguvideo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug, article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="文章未找到" description="您访问的文章不存在或已被移除。" />
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-5">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '蘑菇视频',
      logo: {
        '@type': 'ImageObject',
        url: 'https://lygshjx.com.cn/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://lygshjx.com.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://lygshjx.com.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schemaData={articleSchema}
        pageType="article"
      />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: category?.name || '分类', path: `/category/${article.category}` },
            { name: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {article.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.publishDate}</span>
                <span>·</span>
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>

            <div className="relative w-full h-[300px] md:h-[450px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 蘑菇视频官网`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-16 pt-10 border-t border-background-200">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all group cursor-pointer"
                    >
                      <div className="relative w-full h-40 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          title={`${ra.title} - 蘑菇视频`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
                          <span>{ra.publishDate}</span>
                          <span>·</span>
                          <span>{ra.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </div>
      <Footer />
    </>
  );
}